import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  Zap,
  Ruler,
  Eye,
  Hash,
  Brain,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const InspectionTime = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game phases: intro, tutorial, play, results
  const [gameMode, setGameMode] = useState("intro");
  const [gameCompleted, setGameCompleted] = useState(false);

  // Difficulty: easy (lines), standard (2 shapes), advanced (3 shapes)
  const [difficulty, setDifficulty] = useState("standard");

  // Score & Leaderboard
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // Feedback Overlay
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Instructions Modal
  const [showInstructions, setShowInstructions] = useState(false);

  // Basic game state
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);

  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Timer
  const [timeLeft, setTimeLeft] = useState(60);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const gameActive = useRef(false);

  // Reaction time tracking
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);

  // Timestamps
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // Stimulus display
  const [showStimuli, setShowStimuli] = useState(false);

  // We'll store an array of shape data for each round
  // e.g. [{ type: 'line', size: 120 }, { type: 'line', size: 100 }, ... ]
  const [shapes, setShapes] = useState([]); // array of shape objects
  const [correctIndex, setCorrectIndex] = useState(null); // which shape is largest?

  // Difficulty settings: how long shapes remain visible, total time, etc.
  const difficultySettings = {
    easy: {
      timeLimit: 60,
      inspectionTime: 1000,
    },
    standard: {
      timeLimit: 60,
      inspectionTime: 600,
    },
    advanced: {
      timeLimit: 60,
      inspectionTime: 300,
    },
  };

  // Tutorial
  const [tutorialStep, setTutorialStep] = useState(0);

  // Colors for the game
  const primaryGradient = "from-red-500 to-orange-600";
  const textAccent = "text-red-300";
  const highlightColor = "bg-gradient-to-r from-red-500 to-orange-600";

  // Fetch high scores
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "inspection-time"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const levelKey = difficulty.toString(); // 'easy'|'standard'|'advanced'
          const scores =
            data.leaderboards && data.leaderboards[levelKey]
              ? data.leaderboards[levelKey]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Timer logic
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Start game
  const startGame = () => {
    setScore(0);
    setCurrentRound(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setResponseTimes([]);
    setAverageResponseTime(0);

    setTimeLeft(difficultySettings[difficulty].timeLimit);
    setIsPlaying(true);
    gameActive.current = true;
    setGameStartTime(Date.now());
    setGameEndTime(null);

    startNewRound();
  };

  // End game
  const endGame = () => {
    setIsPlaying(false);
    gameActive.current = false;
    clearInterval(timerRef.current);
    setGameEndTime(Date.now());
    setGameCompleted(currentRound >= totalRounds || timeLeft <= 1);

    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    setGameMode("results");
  };

  /*
    generateShapes(difficulty):
    - For 'easy', we'll do 2 "lines" with random sizes
    - For 'standard', do 2 shapes (circle, square, etc.)
    - For 'advanced', do 3 shapes
  */
  const generateShapes = (diff) => {
    if (diff === "easy") {
      // 2 lines
      const sizes = [
        100 + Math.floor(Math.random() * 40),
        100 + Math.floor(Math.random() * 40),
      ];
      // We only track type = 'line' here
      return sizes.map((sz) => ({ type: "line", size: sz }));
    } else if (diff === "standard") {
      // 2 shapes, let's randomize type for variety
      // Possible types: circle, square
      const shapeTypes = ["circle", "square"];
      const shapesArr = [0, 1].map(() => ({
        type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
        size: 50 + Math.floor(Math.random() * 60), // random radius or side
      }));
      return shapesArr;
    } else {
      // 'advanced': 3 shapes
      // Let's pick from circle, square, triangle, star, etc. for variety
      const shapeTypes = ["circle", "square", "triangle"];
      const shapesArr = [0, 1, 2].map(() => ({
        type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
        size: 40 + Math.floor(Math.random() * 60),
      }));
      return shapesArr;
    }
  };

  /*
    We define the "largest" as whichever shape has the biggest "size".
    For lines, size = line height.
    For circles, size might be the radius (or diameter).
    For squares/triangles, assume "size" is a side length. 
    The user must identify which shape was largest by area or dimension.
    For simplicity, let's just treat "size" as the relevant measure and pick the max.
  */

  // Start a new round
  const startNewRound = () => {
    if (!gameActive.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    // generate shapes for this round
    const newShapes = generateShapes(difficulty);
    setShapes(newShapes);

    // find index of largest shape by .size
    let maxIndex = 0;
    for (let i = 1; i < newShapes.length; i++) {
      if (newShapes[i].size > newShapes[maxIndex].size) {
        maxIndex = i;
      }
    }
    setCorrectIndex(maxIndex);

    setShowStimuli(true);
    setCurrentRound((prev) => prev + 1);

    setResponseStartTime(Date.now());

    // hide shapes after inspectionTime
    const inspect = difficultySettings[difficulty].inspectionTime;
    setTimeout(() => {
      setShowStimuli(false);
    }, inspect);
  };

  // Handle user guess
  const handleGuess = (guessIndex) => {
    if (!isPlaying) return;

    const rt = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, rt]);

    const isCorrect = guessIndex === correctIndex;
    if (isCorrect) {
      setCorrectResponses((prev) => prev + 1);

      // Scoring approach
      let basePoints = 15; // each correct
      let speedBonus = 1; // default minimal bonus

      // Tighter breakpoints
      if (rt < 250) {
        speedBonus = 10;
      } else if (rt < 350) {
        speedBonus = 8;
      } else if (rt < 500) {
        speedBonus = 6;
      } else if (rt < 1000) {
        speedBonus = 4;
      } else if (rt < 1500) {
        speedBonus = 2;
      } else {
        speedBonus = 1;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);

      setFeedbackCorrect(true);
      setFeedbackMessage("Correct!");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 600);
    } else {
      setIncorrectResponses((prev) => prev + 1);
      // small penalty
      setScore((prev) => (prev > 0 ? prev - 5 : 0));

      setFeedbackCorrect(false);
      setFeedbackMessage("Incorrect");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 600);
    }

    if (currentRound < totalRounds) {
      setTimeout(() => {
        startNewRound();
      }, 600);
    } else {
      setTimeout(() => {
        endGame();
      }, 600);
    }
  };

  // Save to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "inspection-time",
        gameId: "inspection-time",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        correctResponses,
        incorrectResponses,
        averageResponseTime,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 References
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "inspection-time"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Composite comparator: score > avg RT > fewer errors
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.averageResponseTime !== b.averageResponseTime)
          return a.averageResponseTime - b.averageResponseTime;
        return (a.incorrectResponses || 0) - (b.incorrectResponses || 0);
      };

      // 🔁 Transaction to update leaderboard + user
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(gameData, currentLeaderboard[4]) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 📜 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset state
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // Helper to render each shape in the UI
  // We only do something special if showStimuli=true
  const renderShape = (shapeObj, index) => {
    if (!showStimuli) {
      // if stimuli are hidden, show nothing (or a placeholder)
      return (
        <div
          key={index}
          style={{ width: 50, height: 50 }}
          className="bg-slate-600 rounded flex items-center justify-center"
        >
          ?
        </div>
      );
    }

    // Render actual shapes:
    // For lines, we can do a vertical bar with "height = shapeObj.size"
    // For circles, "width & height = shapeObj.size * 2" plus border-radius
    // For squares, "width & height = shapeObj.size"
    // For triangle, we can do some CSS trick.
    // Simplify here: just show a <div> with text or something to represent them visually.

    switch (shapeObj.type) {
      case "line":
        return (
          <div
            key={index}
            style={{
              width: "6px",
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)", // tailwind red-400/70
              borderRadius: "3px",
              transition: "height 0.2s",
            }}
          />
        );
      case "circle":
        return (
          <div
            key={index}
            style={{
              width: `${shapeObj.size}px`,
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)",
              borderRadius: "9999px",
              transition: "all 0.2s",
            }}
          />
        );
      case "square":
        return (
          <div
            key={index}
            style={{
              width: `${shapeObj.size}px`,
              height: `${shapeObj.size}px`,
              backgroundColor: "rgba(248,113,113,0.7)",
              borderRadius: "4px",
              transition: "all 0.2s",
            }}
          />
        );
      case "triangle":
        // quick CSS triangle: width=0, height=0, use border
        return (
          <div
            key={index}
            style={{
              width: 0,
              height: 0,
              borderLeft: `${shapeObj.size}px solid transparent`,
              borderRight: `${shapeObj.size}px solid transparent`,
              borderBottom: `${
                shapeObj.size * 1.5
              }px solid rgba(248,113,113,0.7)`,
              transition: "all 0.2s",
            }}
          />
        );
      default:
        return (
          <div
            key={index}
            style={{
              width: 50,
              height: 50,
              backgroundColor: "rgba(248,113,113,0.7)",
            }}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className={`text-xl md:text-2xl font-bold ${textAccent}`}>
              Inspection Time
            </h1>
            <div className="p-2 bg-red-500/10 rounded-full">
              <Eye className="text-red-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <Eye className="text-red-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      Inspection Time Task
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    <strong>Inspection Time</strong> tests your ability to
                    quickly identify which object (line or shape) is
                    bigger—after a very short flash. You must pick the largest
                    one before time runs out!
                  </p>
                  <p>
                    Different difficulty levels change the type of objects and
                    how many are shown. Quick, accurate responses will yield
                    higher scores.
                  </p>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-600 text-white py-3 rounded-lg font-medium hover:brightness-110 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Eye size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Inspection Time
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  A rapid visual discrimination game that challenges your
                  processing speed. Determine which object is largest—before it
                  vanishes!
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Visual Processing
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Sharpen your ability to differentiate stimuli quickly
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Ruler size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Swift Judgements
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Each trial is only visible briefly; pick the largest
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Hash size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Processing Speed
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Practice makes perfect: faster and more accurate
                  </p>
                </div>
              </div>

              {/* Difficulty selection & Start */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? `bg-gradient-to-r from-red-500 to-orange-600 text-white`
                            : `bg-slate-700 text-slate-300 hover:bg-slate-600`
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* Leaderboard */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Resp. Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Errors
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((hs, index) => {
                          let formattedName = hs.userName || "Anonymous";
                          if (formattedName.includes(" ")) {
                            const parts = formattedName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={index}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {hs.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.averageResponseTime
                                  ? `${hs.averageResponseTime} ms`
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.incorrectResponses ?? 0}
                              </td>
                              <td className="py-2 px-4 text-right text-red-300 font-medium">
                                {hs.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Inspection Time
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <strong>Inspection Time</strong> tasks you with
                        discerning which object (line or shape) is largest
                        within a brief flash (the “inspection time”). This tests
                        and trains your rapid
                        <em> perceptual processing</em>.
                      </p>
                      <p className="text-slate-300">
                        Research suggests that practicing quick visual
                        discrimination can improve your ability to process
                        details under time pressure— helpful in daily life and
                        recovery scenarios.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        After a short flash, the shapes vanish. Then you must
                        choose which shape was largest. The difference can be
                        subtle at higher difficulties, so stay focused!
                      </p>
                      <p className="text-slate-300">
                        In Easy mode, we compare two lines. In Standard mode, we
                        show two shapes, and Advanced can have three shapes.
                        Pick the biggest one!
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <strong>Scoring</strong>:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>+15 points per correct answer</li>
                          <li>
                            +1 to +10 speed bonus (the faster you respond, the
                            more points)
                          </li>
                          <li>-5 for incorrect guesses</li>
                        </ul>
                      </p>
                      <p className="text-slate-300">
                        <strong>Time</strong>: You have about 60 seconds
                        (depends on difficulty). If time runs out or you
                        complete all rounds, the game ends.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Practice consistently to see improvements in both speed
                        and accuracy. This can help sharpen mental reflexes and
                        attention to detail in real-world situations.
                      </p>
                      <p className="text-slate-300">
                        Ready? Let’s start training!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Gameplay Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-red-500/20 px-4 py-2 rounded-lg">
                  <span className="text-red-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="ml-4 bg-red-500/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center mb-4">
                {/* If advanced, we might say "Which shape was largest?" 
                    Otherwise: "Which shape is bigger?" / "Which line is longer?" */}
                <h3 className="text-white font-medium mb-2">
                  {difficulty === "easy"
                    ? "Which line was longer?"
                    : "Which shape was biggest?"}
                </h3>
              </div>

              {/* Container for shapes. 
                  For easy/standard => 2 shapes, advanced => 3 shapes */}
              <div
                className="flex items-center justify-center gap-8 mb-8 flex-wrap"
                style={{ minHeight: "180px" }}
              >
                {shapes.map((shape, idx) => (
                  <div key={idx}>{renderShape(shape, idx)}</div>
                ))}
              </div>

              {/* Guess Buttons: 
                  - easy/standard => 2 buttons (index 0 or 1)
                  - advanced => 3 buttons (index 0,1,2)
              */}
              <div className="flex gap-4 justify-center mb-8">
                {shapes.map((_, idx) => (
                  <button
                    key={`guess-${idx}`}
                    onClick={() => handleGuess(idx)}
                    disabled={!isPlaying || showFeedback}
                    className="py-3 px-6 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors"
                  >
                    {difficulty === "easy"
                      ? idx === 0
                        ? "Left"
                        : "Right"
                      : `Choice ${idx + 1}`}
                  </button>
                ))}
              </div>

              {/* Feedback Overlay */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                  >
                    <div className="text-center">
                      <div
                        className={`text-3xl font-bold ${
                          feedbackCorrect ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {feedbackMessage}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Stats */}
              <div className="mt-4 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">
                    Avg. Time (ms)
                  </div>
                  <div className="text-white font-medium">
                    {responseTimes.length > 0
                      ? Math.round(
                          responseTimes.reduce((a, b) => a + b, 0) /
                            responseTimes.length
                        )
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to end the game early?"
                      )
                    ) {
                      endGame();
                    }
                  }}
                  className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                >
                  End Game
                </button>
              </div>
            </div>
          )}

          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You’ve finished the Inspection Time challenge on {difficulty}{" "}
                  difficulty.
                </p>
              </div>

              {/* Score & Performance Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Left Column: Basic Stats */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-red-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-red-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors</span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {averageResponseTime > 0
                          ? `${averageResponseTime} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Analysis or Observations */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-red-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Example: Accuracy bar */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-orange-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Observations */}
                    <ul className="space-y-2 text-sm">
                      <li className="text-slate-300">
                        <strong>High Accuracy</strong>
                        {" => "} you discriminated shapes quickly.
                      </li>

                      <li className="text-slate-300">
                        <strong>Faster Responses</strong> {" => "} better speed
                        bonus & higher scores.
                      </li>
                      <li className="text-slate-300">
                        Keep practicing to lower your average response time
                        further.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bottom Buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionTime;
